import { BasePage } from '../BasePage/BasePage'
import { Breadcrumb } from 'src/components/Breadcrumb/Breadcrumb'
import { useAppContext } from 'src/Context'
import { SectionTitle, SectionTitleSize, SectionTitleStyle } from 'src/components/SectionTitle/SectionTitle'
import { CMSSection, CMSSectionBackground } from 'src/components/CMSSection/CMSSection'

import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { API_URL, formatDate, formatNumber } from 'src/util'
import { Button } from 'src/wizard/Button/Button'
import { Text } from 'src/components/Text/Text'

import styles from './ContractProgramme.module.css'
import { routes } from 'src/routes'
import { NumberField } from 'src/components/NumberField/NumberField'
import { Programme } from 'src/types'
import { readReportSubmitted } from 'src/components/SubmissionNotice/SubmissionNotice'

// BUG #32 (regression fixed): b069cb1 removed the unconditional #61
// workMonth() label shift on the premise that every stored key is the work
// month. That is only true of programmes saved after the generator fix; a
// pre-#32 programme's keys really are the report-due month, one ahead of the
// work they plan. This page now translates keys through the contract's own
// data-derived offset (programmeKeyOffset — the same derivation the server
// guard uses), so both keying schemes label and align correctly. The pure
// helpers live in src/programmeKeys.ts, shared with ContractReport.tsx and
// mirrored from backend/imports/api/util/programmeKeys.ts — the three must
// stay in lockstep (BUG #36's dead-end otherwise).
import {
  periodMonthIndex,
  shiftPeriod,
  contractEndMonth,
  programmeKeyOffset,
  workMonthForKey,
  planKeyForWorkMonth
} from 'src/programmeKeys'

type LabelProps = {
  label: string
  required: boolean
  children: React.ReactNode
}

function Label(props: LabelProps) {
  return (
    <label className={styles.label}>
      <span className={styles.question}>{props.label}{props.required ? '*' : ''}</span>
      {props.children}
    </label>
  )
}


function HelpText({text}: {text: string}) {
  return <span className={styles.help}>{text}</span>
}


function ContractProgramme() {
  const params = useParams()
  const context = useAppContext()
  const navigate = useNavigate()
  const location = useLocation()
  const contract = context.contracts.find(c => c._id === params.contract)

  const [state, setState] = useState<Partial<Programme>>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // BUG #36: shown when a save succeeded but the server kept the agreed figure
  // for months already reported (ignoredPeriods in the response)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (contract && contract.programme) {
      // BUG #35: load the stored programme exactly as it is, and on a copy.
      //
      // This used to walk the already-reported periods and overwrite the
      // planned figure with whatever the contractor actually completed
      // whenever they came in under plan. Three things were wrong with that:
      //  - it wrote through a reference (`initial = contract.programme`), so
      //    `context.contracts` was corrupted for the rest of the session and
      //    the contractor's dashboard could never show a shortfall;
      //  - it lowered the plan to match the actual, which is not what was
      //    planned; and
      //  - `apiProgramme` persists the programme wholesale (`$set`), so the
      //    rewritten history was written back to the database on the next
      //    save, erasing what had actually been promised.
      //
      // The "you are behind" feedback that back-fill was trying to give is now
      // shown *alongside* the plan instead of replacing it — see `progress`.
      setState({
        ...contract.programme,
        allocation: {...contract.programme.allocation}
      })
    }
  }, [contract])

  // #54: the report page redirects here after a successful submission when the
  // programme needs rebalancing — say so, or the redirect reads as an error.
  useEffect(() => {
    const submitted = readReportSubmitted(location.state)
    if (submitted === null) return

    let message = `Your KPI report for ${submitted.period} has been submitted.`
    if (submitted.programmeNeedsAttention)
      message +=
        " Your programme no longer accounts for all of your contract's jobs — please rebalance the remaining months below."
    setNotice(message)
    // fire once, on mount, from the state the navigation arrived with
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (contract === undefined)
    return null

  const storedAllocation = contract.programme?.allocation ?? {}
  const storedKeys = Object.keys(storedAllocation)

  // BUG #36: the submissions serialised onto the contract are already filtered
  // to archived === false, so `submitted != null` is the same "this month has
  // been reported" test the server's lockedProgrammePeriods() applies.
  const submittedCompleted = new Map<string, number | undefined>(
    contract.submissions
      .filter(s => s.submitted != null)
      .map(s => [s.period, s.completed])
  )

  // #53: the third argument is the tail discriminator — the contract's last
  // work month, read with the same +2h nudge as the server, so this page and
  // the server guard derive one and the same offset
  const offset = programmeKeyOffset(
    storedKeys,
    contract.periods[0],
    contractEndMonth(contract.endDate)
  )

  // BUG #36: the exact locked set the server's lockedProgrammePeriods() will
  // derive for this contract — a stored key is locked when its own month or
  // its offset counterpart carries a submitted report. Must stay in lockstep
  // with backend/server/api/contracts.ts.
  const locked = new Set<string>()
  if (submittedCompleted.size > 0) {
    for (const key of storedKeys) {
      if (submittedCompleted.has(key)) {
        locked.add(key)
        continue
      }
      if (offset !== 0) {
        const work = shiftPeriod(key, -offset)
        if (work !== null && submittedCompleted.has(work)) locked.add(key)
      }
    }
  }

  // BUG A (#32×#35): render the union of the stored allocation keys and the
  // expected range, sorted, so every stored key has a visible, labelled
  // field. Rendering only the expected range made stray keys invisible while
  // `total` still counted them, so the page reported an excess no field on
  // screen carried and no one could zero.
  //
  // #32 REGRESSION: the fields are allocation KEYS, so on an offset programme
  // the expected range is the generated work months translated into key space
  // (work month + offset). Unioning the raw work-month range with due-keyed
  // stored keys produced a spurious blank leading field that planned a month
  // before the contract, and pointed distribute() at keys the stored map
  // doesn't use. offset 0 is the identity — unchanged for programmes keyed by
  // work month.
  const expectedKeys = contract.contractProgrammePeriods.map((p) =>
    planKeyForWorkMonth(p, offset)
  )
  const expected = new Set<string>(expectedKeys)
  const renderPeriods = Array.from(
    new Set([...storedKeys, ...expectedKeys])
  ).sort((a, b) => (periodMonthIndex(a) ?? 0) - (periodMonthIndex(b) ?? 0))

  // #39: the actual completed figure counted at a locked key — the submitted
  // report for the work month the key plans (its offset counterpart), or null
  // when no usable actual exists. Mirror of the same lookup in apiProgramme.
  const lockedActual = (key: string): number | null => {
    const work = offset === 0 ? key : shiftPeriod(key, -offset)
    const actual = work === null ? undefined : submittedCompleted.get(work)
    return typeof actual === 'number' && isNaN(actual) === false ? actual : null
  }

  // #39 BUSINESS-RULE CHANGE (a sensible default pending EP confirmation): the
  // delivery total counts what was ACTUALLY completed for months whose plan is
  // locked, plus the plan for the months still open — the contract must still
  // deliver `quantity` jobs given what has really happened. Counting the
  // locked *plan* instead (the old rule) pinned the forward allocation at
  // quantity − locked plan, so a behind-schedule contractor could never re-add
  // the shortfall and the page's own "amend your programme" instruction was
  // impossible to follow. Locked plans stay stored untouched — they are
  // history (#35/#36) — they just stop double-counting against delivery.
  // Must stay bit-identical to the total apiProgramme validates.
  const total = renderPeriods.reduce((sum, key) => {
    const plan = state.allocation?.[key] ?? 0
    if (locked.has(key)) return sum + (lockedActual(key) ?? plan)
    return sum + plan
  }, 0)

  // BUG #35: planned vs actually completed across the months already reported.
  // Surfaced per month and summarised below the grid, so falling behind is
  // still visible without the plan being quietly rewritten to hide it.
  // #36/#39: a report's plan lives at its offset counterpart key on legacy
  // programmes, so look the plan up through the same offset the lock uses.
  const progress = contract.submissions.reduce(
    (acc, s) => {
      if (s.completed == null) return acc
      const planKey = offset === 0 ? s.period : shiftPeriod(s.period, offset)
      return {
        planned: acc.planned + (planKey !== null ? state.allocation?.[planKey] ?? 0 : 0),
        completed: acc.completed + s.completed
      }
    },
    {planned: 0, completed: 0}
  )
  const behindBy = Math.max(0, progress.planned - progress.completed)

  // evenly distribute the outstanding jobs between the remaining periods
  // any extras automatically add to the first months
  const distribute = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    // BUG #35: `{...state}` is a shallow copy, so writing to `set.allocation`
    // used to write straight through to the object held in state — and, before
    // the effect above was fixed, to `context.contracts` as well. Copy the
    // allocation too.
    const set = {...state, allocation: {...state.allocation}}

    // BUG #36: distribute only into months the page offers as editable — the
    // expected KEYS that are neither guard-locked nor already carrying a
    // report (drafts included, which stay read-only as they always have).
    // #32 REGRESSION: expectedKeys, not the raw work-month range — on an
    // offset programme distributing into work-month keys would mix the two
    // keying schemes in one stored map, which nothing downstream can read.
    // A report is matched by the work month the key plans, the same identity
    // the field's read-only test uses.
    const between = expectedKeys.filter(p =>
      locked.has(p) === false &&
      contract.submissions.find(s => s.period === workMonthForKey(p, offset)) === undefined
    )
    if (between.length === 0)
      return false

    // #39: what is left to plan is the contract quantity less what the locked
    // months actually delivered (falling back to their stored plan when no
    // actual exists) and less anything allocated to visible fields outside the
    // distribution range — e.g. a legacy phantom month (BUG A), which the
    // contractor can zero to release its jobs. This is the same effective
    // total the server validates.
    const committed = renderPeriods
      .filter(p => between.includes(p) === false)
      .reduce((a, p) => {
        const plan = set.allocation[p] ?? 0
        return a + (locked.has(p) ? (lockedActual(p) ?? plan) : plan)
      }, 0)
    const remaining = Math.max(0, (contract.quantity as number) - committed)

    const each = Math.floor(remaining / between.length)
    const extra = remaining - each * between.length
    for (const [index, period] of between.entries()) {
      set.allocation[period] = each
      if (index < extra)
        ++set.allocation[period]
    }

    setState(set)
    return false
  }

  const submit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setError(null)
    setNotice(null)
    setSubmitting(true)

    try {
      if (contract === undefined)
        return

      // hc will send quantity as a string instead of an int
      // #39: `total` counts actuals for locked months and the plan for open
      // months — the same rule the server checks, kept bit-identical so the
      // page never lets through a save the server refuses (or vice versa).
      if (contract.quantity && total != contract.quantity) {
        const text = total < contract.quantity ? 'short' : 'over'
        const diff = Math.abs(contract.quantity - total)
        setError(`Your programme must account for all ${formatNumber(contract.quantity)} jobs — jobs completed in the months already reported plus those planned for the remaining months. It is currently ${formatNumber(diff)} jobs ${text}.`)
        return
      }

      const data = new URLSearchParams()
      data.append('username', context.user?.username as string)
      data.append('contractId', contract._id)
      data.append('state', JSON.stringify(state))

      const response = await fetch(`${API_URL}programme`, {
        method: 'post',
        headers: {
          Authorization: `Token ${context.user?.token}`
        },
        body: data
      })

      if (response.ok === false) {
        // #40-style: the server now puts the reason in the response body
        // (see handle() in backend/server/api.ts) — statusText is just
        // "Bad Request" and hides it.
        let message = ''
        try {
          const body = await response.json()
          if (typeof body?.message === 'string') message = body.message
        } catch {
          // no JSON body — fall through to the generic message
        }
        setError(message || 'The programme could not be saved. Please try again.')
        return
      }

      const json = await response.json()

      if (json.success === false) {
        setError(json.message)
        return
      }

      // update local state -- modify by reference
      const contracts = [...context.contracts]
      const index = contracts.findIndex(c => c._id === contract._id)

      const updated = {...json.contract}
      if (index >= 0) {
        contracts[index] = {
          ...updated,
          siteStartDate: new Date(updated.siteStartDate as string),
          endDate: new Date(updated.endDate as string),
          reportStartDate: new Date(updated.reportStartDate as string),
          dueFirst: new Date(updated.dueFirst as string),
          dueNext: new Date(updated.dueNext as string),
          createdAt: new Date(updated.createdAt as string)
        }
      }
      else { // this should never happen, but if it does just add it on the end
        contracts.push({
          ...updated,
          siteStartDate: new Date(updated.siteStartDate as string),
          endDate: new Date(updated.endDate as string),
          reportStartDate: new Date(updated.reportStartDate as string),
          dueFirst: new Date(updated.dueFirst as string),
          dueNext: new Date(updated.dueNext as string),
          createdAt: new Date(updated.createdAt as string)
        })
      }
      context.setContext({...context, contracts})

      // BUG #36: the server retains the agreed figure for any month already
      // reported and reports which via `ignoredPeriods`/`message`. Surface
      // that instead of silently navigating away when the saved programme
      // differs from the posted one — the load effect above refreshes the
      // fields from the saved programme once the context updates.
      if (Array.isArray(json.ignoredPeriods) && json.ignoredPeriods.length > 0) {
        setNotice(
          (json.message as string | undefined) ??
          'Some months had already been reported, so the agreed figures for them were kept.'
        )
        return
      }

      navigate(routes.member.path)
    }
    catch {
      setError('A network error occurred. Please check your internet connection and try again.')
      return
    }
    finally {
      setSubmitting(false)
    }
  }

  const text = contract?.quantity ? (total < contract.quantity ? 'short' : 'over') : ''
  const diff = contract?.quantity ? (Math.abs(contract.quantity - total)) : 0

  // BUG #35: with the plan no longer rewritten to match the actuals, a saved
  // programme normally balances exactly, so "0 jobs over" would be the usual
  // reading. Say it plainly instead. #39: "accounts for", because the total
  // counts completed jobs for reported months and planned jobs for open ones.
  const allocationNote = diff === 0
    ? `Your programme accounts for all ${formatNumber(contract.quantity)} jobs.`
    : `Your programme must account for ${formatNumber(contract.quantity)} jobs — jobs completed in the months already reported plus those planned for the remaining months — and is currently ${formatNumber(diff)} jobs ${text}.`

  return (
    <BasePage>
      <Breadcrumb/>

      <CMSSection
        primary={
          <>
            <SectionTitle title={`${contract.name} Programme`} size={SectionTitleSize.Small} style={SectionTitleStyle.Dark} />

            <Text>{`Your contract is scheduled to complete ${formatNumber(contract.quantity)} jobs by ${formatDate(contract.endDate, {minute: undefined, hour: undefined})}. ${allocationNote} You must submit a programme of how many jobs you will complete each month. If you fall behind on your programme you must amend your programme.`}</Text>
            <form>
              <div className={styles.grid}>
                {renderPeriods.map(p => {
                  // #32 REGRESSION: everything about this field hangs off the
                  // month the KEY plans work for, not the raw key. b069cb1
                  // removed the unconditional #61 workMonth() shift on the
                  // premise that every key is work-month-keyed; a programme
                  // saved before it is keyed by the report-due month, one
                  // ahead of the work, so the raw key prompted July's figures
                  // under "August". The shift is per-contract now, driven by
                  // the same offset the lock and the totalling rule already
                  // derive from the stored data — an unconditional shift
                  // either way is wrong for the other half of the contracts
                  // (0b05d52 shifted all, b069cb1 shifted none). The label,
                  // the submission this field is paired with, and the
                  // "completed" annotation all use this identity, so the
                  // figure under a heading is always that month's figure.
                  const work = workMonthForKey(p, offset)
                  const submission = contract.submissions.find(s => s.period === work)
                  const planned = state.allocation?.[p]
                  const isLocked = locked.has(p)

                  // BUG #36: read-only must cover at least the server's locked
                  // set, or the page offers an edit the guard will revert and
                  // the save is refused over a value the contractor cannot
                  // change (the unactionable-refusal dead-end). Months whose
                  // work month carries a report (drafts included) stay
                  // read-only as they always were — stricter than the guard
                  // is safe, looser is not.
                  const readonly = isLocked || submission !== undefined

                  // BUG #35/#39: the actual is reported next to the plan; it
                  // never replaces it. For a locked key show the actual the
                  // totalling rule counts at this key; otherwise the (draft)
                  // report for the work month this key plans. Each field shows
                  // its own work month's actual exactly once.
                  const completed = isLocked
                    ? lockedActual(p)
                    : submission?.completed ?? null
                  const shortfall = (completed != null && planned != null) ? planned - completed : null

                  // BUG A: a stored key outside the expected range — the old
                  // phantom 13th month or an offset leading key from a
                  // pre-#32 programme. It stays visible and (when not locked)
                  // editable, so its jobs can be zeroed back into real months.
                  const legacy = expected.has(p) === false

                  const label = work

                  return (
                    <div key={p}>
                      <Label label={label} required={true}>
                        <NumberField
                          onChange={e => setState({...state, allocation: {...state.allocation, [p]: e.target.value.length ? parseInt(e.target.value) : 0}})}
                          value={planned ?? ''}
                          required={true}
                          readonly={readonly}
                        />
                        {completed != null ?
                          <span className={shortfall !== null && shortfall > 0 ? styles.behind : styles.onTarget}>
                            {`${formatNumber(completed)} completed${shortfall !== null && shortfall > 0 ? ` — ${formatNumber(shortfall)} behind plan` : ''}`}
                          </span>
                          :
                          null
                        }
                        {readonly ?
                          <HelpText text={'Past entries cannot be modified.'}/>
                          :
                          legacy ?
                            <HelpText text={'Left over from an earlier programme — set to 0 to move these jobs into the scheduled months.'}/>
                            :
                            null
                        }
                      </Label>
                    </div>
                  )
                })}
              </div>

              { behindBy > 0 ?
                <Text className={styles.behindNotice}>
                  {`You have completed ${formatNumber(progress.completed)} of the ${formatNumber(progress.planned)} jobs your programme scheduled for the months already reported — ${formatNumber(behindBy)} behind schedule. Past months show the plan as it was agreed and cannot be changed; use the remaining months to show when the outstanding work will be done.`}
                </Text>
                :
                null
              }

              { error ?
                <Text className={styles.error}>{error}</Text>
                :
                null
              }
              { notice ?
                <Text className={styles.notice}>{notice}</Text>
                :
                null
              }
              <div className={styles.buttons}>
                <Button label={'Distribute Equally'} onClick={distribute} colour={'blue'} formNoValidate={true}/>
                <Button onClick={submit} name={'submit'} colour={'green'} label={'Submit'} disabled={submitting || contract === undefined}/>
              </div>
            </form>
          </>
        }
        background={CMSSectionBackground.White}
      />
    </BasePage>
  )
}

export { ContractProgramme }


